import { useState, useEffect } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { CTeDto, modalOptions, tipoCteOptions, tipoDocumentoOptions, tipoServicoOptions, tipoTomadorOptions, unidadeMedidaOptions } from "../CTe.Dto";
import { CTeBuscarXML, CTeFiltrar, CTeImprimir } from "src/services/Fiscal.Service";
import { showToast } from "src/componentes";
import { FormatarCEP, FormatarCPF, FormatarCNPJ, FormatarDataHoraDefault, FormatarTelefone, MapearErrosServidor, SoNumeros } from "src/hook/Functions";

const CTeVisualizaCode = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  const [cte, setCte] = useState<CTeDto | undefined>(location.state?.cte);
  const [loading, setLoading] = useState(!cte);
  const [downloadingXml, setDownloadingXml] = useState(false);
  const [printing, setPrinting] = useState(false);

  useEffect(() => {
    if (!cte && id) {
      loadCTe(Number(id));
    }
  }, [id, cte]);

  const loadCTe = async (cteId: number) => {
    setLoading(true);
    try {
      const response = await CTeFiltrar({ id: cteId });
      if (response.data.success && response.data.data.length > 0) {
        setCte(response.data.data[0]);
      } else {
        MapearErrosServidor(response.data.notifications, showToast);
      }
    } catch (error) {
      console.error("Erro ao carregar dados do CT-e:", error);
      showToast({ message: "Erro ao carregar dados do CT-e", variant: "Danger" });
    } finally {
      setLoading(false);
    }
  };

  const onVoltar = () => {
    navigate("/fiscal/cte");
  };

  const onBaixarXml = async () => {
    if (!cte?.id) return;
    setDownloadingXml(true);
    try {
      const nomeArquivo = `CTe_${cte.numeroCte}`;
      const response = await CTeBuscarXML([cte.id], nomeArquivo);
      const blob = new Blob([response.data], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${nomeArquivo}.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Erro ao baixar XML do CT-e:", error);
      showToast({ message: "Erro ao baixar XML", variant: "Danger" });
    } finally {
      setDownloadingXml(false);
    }
  };

  const onImprimir = async () => {
    if (!cte?.id) return;
    setPrinting(true);
    try {
      const response = await CTeImprimir(cte.id);
      if (response.data instanceof Blob) {
        const pdfBlob = new Blob([response.data], { type: "application/pdf" });
        const pdfUrl = URL.createObjectURL(pdfBlob);
        const isMobile = /Android|iPhone/i.test(navigator.userAgent);

        if (isMobile) {
          window.open(pdfUrl, "_blank");
        } else {
          const iframe = document.createElement("iframe");
          iframe.style.position = "fixed";
          iframe.style.right = "0";
          iframe.style.bottom = "0";
          iframe.style.width = "0";
          iframe.style.height = "0";
          iframe.src = pdfUrl;
          document.body.appendChild(iframe);

          iframe.onload = () => {
            iframe.contentWindow?.focus();
            iframe.contentWindow?.print();
          };
        }
      } else {
        MapearErrosServidor(response.data.notifications, showToast);
      }
    } catch (error) {
      console.error("Erro ao imprimir DACTE:", error);
      showToast({ message: "Erro ao imprimir DACTE", variant: "Danger" });
    } finally {
      setPrinting(false);
    }
  };

  const copiarTexto = (texto: string, label = "Texto") => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(texto);
      showToast({ message: `${label} copiado(a) para a área de transferência!`, variant: "Success" });
    }
  };

  const formatarDocumento = (doc?: string) => {
    if (!doc) return "-";
    const limpo = SoNumeros(doc);
    if (limpo.length === 11) return FormatarCPF(limpo);
    if (limpo.length === 14) return FormatarCNPJ(limpo);
    return doc;
  };

  const formatarMoeda = (valor?: number) => {
    const num = Number(valor) || 0;
    return num.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
  };

  const getTipoCteLabel = (tipo?: number) => {
    const opt = tipoCteOptions.find((o) => o.value === tipo);
    return opt ? opt.label : tipo != null ? String(tipo) : "-";
  };

  const getTipoServicoLabel = (servico?: number) => {
    const opt = tipoServicoOptions.find((o) => o.value === servico);
    return opt ? opt.label : servico != null ? String(servico) : "-";
  };

  const getModalLabel = (modal?: number) => {
    const opt = modalOptions.find((o) => o.value === modal);
    return opt ? opt.label : modal != null ? String(modal) : "-";
  };

  const getTipoTomadorLabel = (tomador?: number) => {
    const opt = tipoTomadorOptions.find((o) => o.value === tomador);
    return opt ? opt.label : tomador != null ? String(tomador) : "-";
  };

  const getTipoDocumentoLabel = (tipo?: number) => {
    const opt = tipoDocumentoOptions.find((o) => o.value === tipo);
    return opt ? opt.label : tipo != null ? String(tipo) : "Documento";
  };

  const getUnidadeMedidaLabel = (codigo?: string) => {
    const opt = unidadeMedidaOptions.find((o) => o.value === codigo);
    return opt ? opt.label : codigo || "-";
  };

  return {
    cte,
    loading,
    downloadingXml,
    printing,
    onVoltar,
    onBaixarXml,
    onImprimir,
    copiarTexto,
    formatarDocumento,
    formatarMoeda,
    FormatarCEP,
    FormatarTelefone,
    FormatarDataHoraDefault,
    getTipoCteLabel,
    getTipoServicoLabel,
    getModalLabel,
    getTipoTomadorLabel,
    getTipoDocumentoLabel,
    getUnidadeMedidaLabel,
  };
};

export default CTeVisualizaCode;
